import { Col, Form, Input, InputNumber, Modal, Radio, Row, Switch, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { WAIVE_MODE_OPTIONS } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { PenaltyType } from 'pages/star-shared/types';

interface Props {
  open: boolean;
  penaltyType: PenaltyType | null;
  onClose: () => void;
  onSuccess: () => void;
}

const PenaltyTypeModal = ({ open, penaltyType, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: penaltyType?.name || '',
      description: penaltyType?.description || '',
      starCost: penaltyType?.starCost ?? 10,
      waiveMode: penaltyType?.waiveMode || 'DIRECT',
      active: penaltyType?.active ?? true,
      sort: penaltyType?.sort ?? 0,
    });
  }, [open, penaltyType, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      starCost: values.starCost,
      waiveMode: values.waiveMode,
      active: !!values.active,
      sort: values.sort ?? 0,
    };
    setLoading(true);
    try {
      if (penaltyType) {
        await AxiosUtils.patch(`${API.starsPenaltyTypes}/${penaltyType.id}`, payload);
        notification.success({ message: 'Đã cập nhật loại phạt.' });
      } else {
        await AxiosUtils.post(API.starsPenaltyTypes, payload);
        notification.success({ message: 'Đã tạo loại phạt.' });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={penaltyType ? 'Sửa loại phạt' : 'Thêm loại phạt'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={560}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Tên loại phạt"
          name="name"
          rules={[
            { required: true, whitespace: true, message: 'Vui lòng nhập tên.' },
            { max: 120, message: 'Tối đa 120 ký tự.' },
          ]}
        >
          <Input placeholder="VD: Đi muộn quá 15 phút" maxLength={120} />
        </Form.Item>
        <Form.Item label="Mô tả" name="description" rules={[{ max: 500, message: 'Tối đa 500 ký tự.' }]}>
          <Input.TextArea rows={2} maxLength={500} showCount placeholder="Khi nào áp dụng" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Số Sao để xoá phạt"
              name="starCost"
              rules={[{ required: true, type: 'integer', min: 0, message: 'Số nguyên ≥ 0.' }]}
            >
              <InputNumber size="large" style={{ width: '100%' }} min={0} precision={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Thứ tự" name="sort">
              <InputNumber size="large" style={{ width: '100%' }} precision={0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Cách xoá phạt"
          name="waiveMode"
          rules={[{ required: true }]}
          extra="Trừ trực tiếp: người dùng bấm xoá là trừ Sao và đóng ngay. Phải kiểm duyệt: trừ Sao trước, quản lý duyệt sau; từ chối thì hoàn Sao."
        >
          <Radio.Group options={WAIVE_MODE_OPTIONS} />
        </Form.Item>
        <Form.Item label="Đang dùng" name="active" valuePropName="checked">
          <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={penaltyType ? 'Lưu' : 'Tạo'}
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default PenaltyTypeModal;
